import React, { ChangeEvent, forwardRef, memo, useCallback, useState } from 'react';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { classnames } from '@src/utils/classnames';
import styled from './Checkbox.scss';
import { Box } from '@src/atoms/Box';
import { CHECKBOX_SIZE_MAP } from '@src/atoms/Checkbox/consts';
import { ICheckboxProps } from './models';
import { useMergedValue } from '@src/utils/useMergedValue';
import { onUnitClickCallback } from '@src/core/Unit';

/**
 * Чекбокс
 */
export const Checkbox = memo(
  forwardRef<HTMLInputElement, ICheckboxProps>(
    (
      {
        id,
        children,
        className,
        disabled,
        value: propsValue,
        view = 'default',
        style,
        onChange,
        defaultChecked = false,
      },
      ref,
    ) => {
      const [value, setValue] = useState(propsValue || defaultChecked);
      const classes = classnames(styled.root, className, disabled && styled.disabled);
      const iconWrapperClasses = classnames(styled.iconWrapper, disabled && styled.iconWrapperDisabled);
      const textSize = CHECKBOX_SIZE_MAP[view];

      const { mergedValue } = useMergedValue({
        value,
        propsValue,
      });
      const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.checked;

          if (propsValue === undefined) {
            setValue(newValue);
          }

          onChange?.(e.target.checked);
        },
        [onChange, propsValue],
      );

      return (
        <Box className={classes} style={style} onClick={onUnitClickCallback(id)}>
          <input
            disabled={disabled}
            type="checkbox"
            className={styled.input}
            checked={mergedValue}
            onChange={handleChange}
            id={id}
            data-id={id}
            ref={ref}
          />
          <label className={styled.label} htmlFor={id}>
            <Box className={iconWrapperClasses}>
              <Icon name="activeCheck" className={styled.icon} />
            </Box>
            <Text id={'children'} size={textSize}>
              {children}
            </Text>
          </label>
        </Box>
      );
    },
  ),
);

Checkbox.displayName = 'Checkbox';
